import { useState } from "react";
import { useAtom, useSetAtom } from "jotai";
import { BugPlay } from "lucide-react";

import { AssessmentLights, ConversationPhase, MessageType } from "shared/types";
import { createMessageEvent, useChannel } from "shared/channel";
import {
  conversationOverrideVar,
  debugMessageOffsetVar,
  debugMessageVar,
  messagesVar,
  noticeboardDataVar,
  showReportVar,
} from "@/variables/globalVar";
import { getQuestion, questionData } from "@/components/debug/questions";

import { useAssessmentLights, useExpertMapConfig } from "shared/hooks";
import { useAccount } from "@/hooks";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuTrigger,
  Input,
} from "shared/components";
import { SendUserMessageDialog } from "./SendUserMessageDialog";

export function DebugMenu() {
  const [dropdownMenuOpen, setDropdownMenuOpen] = useState(false);
  const [addMessageDialogOpen, setAddMessageDialogOpen] = useState(false);

  const { channel } = useChannel();
  const { conversation } = useAccount();

  const expertMap = useExpertMapConfig(conversation)?.enquiry;

  const [noticeboardData, setNoticeboardDataVar] = useAtom(noticeboardDataVar);
  const [debugMessageOffset, setDebugMessageOffsetVar] = useAtom(
    debugMessageOffsetVar,
  );

  const assessmentLights = useAssessmentLights(
    conversation,
    noticeboardData,
    "enquiry",
  );

  const [messages, setMessagesVar] = useAtom(messagesVar);
  const setConversationOverrideVar = useSetAtom(conversationOverrideVar);

  const setShowReportVar = useSetAtom(showReportVar);
  const setDebugMessageVar = useSetAtom(debugMessageVar);

  const handleClickSendMessage = async (message: string) => {
    if (channel && conversation?.id) {
      const messageDescriptor = createMessageEvent({
        conversation_id: conversation.id,
        text: message,
      });
      console.log("<DebugMenu> handleClickSendMessage()", messageDescriptor);
      channel.sendMessage(messageDescriptor);
    }
    setAddMessageDialogOpen(false);
  };

  return (
    <>
      <DropdownMenu onOpenChange={setDropdownMenuOpen} open={dropdownMenuOpen}>
        <DropdownMenuTrigger
          className="text-bodyxs cursor-pointer px-2"
          title="Debug menu"
        >
          <BugPlay />
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem
            onClick={() => {
              setDropdownMenuOpen(false);
              setAddMessageDialogOpen(true);
            }}
          >
            Add user message
          </DropdownMenuItem>

          <DropdownMenuSub>
            <DropdownMenuSubTrigger>Conversation phase</DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                <DropdownMenuRadioGroup
                  onValueChange={(value) => {
                    if (!conversation) return;

                    setConversationOverrideVar(conversation);
                    setConversationOverrideVar((draft) => {
                      if (!draft) return;

                      draft.phase = value;
                    });
                  }}
                  value={conversation?.phase || ""}
                >
                  <DropdownMenuRadioItem value={ConversationPhase.new_conv}>
                    new_conv
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value={ConversationPhase.lot_details}>
                    lot_details
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem
                    value={ConversationPhase.proposal_details}
                  >
                    proposal_details
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value={ConversationPhase.in_progress}>
                    in_progress
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem
                    value={ConversationPhase.ready_for_assessment}
                  >
                    ready_for_assessment
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value={ConversationPhase.report_ready}>
                    report_ready
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value={ConversationPhase.off_topic}>
                    off_topic
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value={ConversationPhase.amendment}>
                    amendment
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value={ConversationPhase.saved_report}>
                    saved_report
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem
                    value={ConversationPhase.sent_to_council}
                  >
                    sent_to_council
                  </DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>

          <DropdownMenuSub>
            <DropdownMenuSubTrigger>Last message offset</DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                <DropdownMenuLabel>
                  Render last message minus offset
                </DropdownMenuLabel>
                <Input
                  min={0}
                  max={(messages || []).length - 1}
                  onChange={(event) => {
                    const value = event.target.value;
                    if (value !== "") {
                      setDebugMessageOffsetVar(parseInt(value));
                    }
                  }}
                  type="number"
                  value={debugMessageOffset}
                />
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>

          <DropdownMenuSub>
            <DropdownMenuSubTrigger>Assessment lights</DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                <DropdownMenuRadioGroup
                  onValueChange={(value) => {
                    const { expert_name = "" } =
                      expertMap?.["assessment_lights"] || {};

                    setNoticeboardDataVar((prev) => {
                      if (!prev[expert_name]) {
                        prev[expert_name] = {
                          created_at: "2025-02-24 02:13:28.523745+00:00",
                          application_id: conversation?.application || "",
                          conversation_id: conversation?.id || "",
                          expert_name: "assessment_lights",
                          message_number:
                            conversation?.last_user_message_number || 0,
                          expert_version: 21,
                          raw_output: value,
                          parsed_output: {},
                          results: {},
                        };
                      } else {
                        prev[expert_name]["raw_output"] = value;
                      }
                    });
                  }}
                  value={assessmentLights}
                >
                  <DropdownMenuRadioItem
                    value={AssessmentLights.NoPermitRequired}
                  >
                    No permit required
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem
                    value={AssessmentLights.MoreInformationRequired}
                  >
                    More information required
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem
                    value={AssessmentLights.RequiresPermit}
                  >
                    Permit required
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value={AssessmentLights.Prohibited}>
                    Prohibited
                  </DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>

          <DropdownMenuItem
            onClick={() => {
              window.localStorage.removeItem("showReport");
              setShowReportVar({
                visible: false,
                conversationId: "",
              });
            }}
          >
            Reset disclaimer acceptance
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuLabel>Questions</DropdownMenuLabel>
          <DropdownMenuItem
            onClick={() => {
              const newMessage = getQuestion(
                conversation?.id || "",
                messages?.length || 0,
                MessageType.CHOICES_QUESTION,
                questionData["single select choices"],
              );
              setDebugMessageVar(newMessage as any);
              setMessagesVar((prev) => [...(prev || []), newMessage as any]);
            }}
          >
            CHOICES_QUESTION - single select choices
          </DropdownMenuItem>

          <DropdownMenuItem
            onClick={() => {
              const newMessage = getQuestion(
                conversation?.id || "",
                messages?.length || 0,
                MessageType.CHOICES_QUESTION,
                questionData["Map, multiple questions"],
              );
              setDebugMessageVar(newMessage as any);
              setMessagesVar((prev) => [...(prev || []), newMessage as any]);
            }}
          >
            CHOICES_QUESTION - Map, multiple questions
          </DropdownMenuItem>

          <DropdownMenuItem
            onClick={() => {
              const newMessage = getQuestion(
                conversation?.id || "",
                messages?.length || 0,
                MessageType.CHOICES_QUESTION,
                questionData["multiple questions linked via dependency"],
              );
              setDebugMessageVar(newMessage as any);
              setMessagesVar((prev) => [...(prev || []), newMessage as any]);
            }}
          >
            CHOICES_QUESTION - multiple questions linked via dependency
          </DropdownMenuItem>

          <DropdownMenuItem
            onClick={() => {
              const newMessage = getQuestion(
                conversation?.id || "",
                messages?.length || 0,
                MessageType.CHOICES_QUESTION,
                questionData["multiple choice question"],
              );
              setDebugMessageVar(newMessage as any);
              setMessagesVar((prev) => [...(prev || []), newMessage as any]);
            }}
          >
            CHOICES_QUESTION - multiple choice question
          </DropdownMenuItem>

          <DropdownMenuItem
            onClick={() => {
              const newMessage = getQuestion(
                conversation?.id || "",
                messages?.length || 0,
                MessageType.CHOICES_QUESTION,
                questionData["trees within the property boundary"],
              );
              setDebugMessageVar(newMessage as any);
              setMessagesVar((prev) => [...(prev || []), newMessage as any]);
            }}
          >
            CHOICES_QUESTION - trees within the property boundary
          </DropdownMenuItem>

          <DropdownMenuItem
            onClick={() => {
              const newMessage = getQuestion(
                conversation?.id || "",
                messages?.length || 0,
                MessageType.TREE_MODULE_QUESTION,
                {
                  destroying: true,
                  pruning: true,
                  digging: true,
                },
              );
              setDebugMessageVar(newMessage as any);
              setMessagesVar((prev) => [...(prev || []), newMessage as any]);
            }}
          >
            Tree activity
          </DropdownMenuItem>

          <DropdownMenuItem
            onClick={() => {
              const newMessage = getQuestion(
                conversation?.id || "",
                messages?.length || 0,
                MessageType.NEARBY_TREES_QUESTION,
                {},
              );

              setDebugMessageVar(newMessage as any);
              setMessagesVar((prev) => [...(prev || []), newMessage as any]);
            }}
          >
            Nearby trees
          </DropdownMenuItem>

          <DropdownMenuItem
            onClick={() => {
              const newMessage = getQuestion(
                conversation?.id || "",
                messages?.length || 0,
                MessageType.REMOVING_OR_PRUNING_QUESTION,
                {},
              );

              setDebugMessageVar(newMessage as any);
              setMessagesVar((prev) => [...(prev || []), newMessage as any]);
            }}
          >
            Removing or pruning
          </DropdownMenuItem>

          <DropdownMenuItem
            onClick={() => {
              const newMessage = getQuestion(
                conversation?.id || "",
                messages?.length || 0,
                MessageType.TREE_DIAMETER_QUESTION,
                {},
              );
              setDebugMessageVar(newMessage as any);
              setMessagesVar((prev) => [...(prev || []), newMessage as any]);
            }}
          >
            Tree diameter
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              const newMessage = getQuestion(
                conversation?.id || "",
                messages?.length || 0,
                MessageType.TREE_DISTANCE_QUESTION,
                {},
              );
              setDebugMessageVar(newMessage as any);
              setMessagesVar((prev) => [...(prev || []), newMessage as any]);
            }}
          >
            Tree distance
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              const newMessage = getQuestion(
                conversation?.id || "",
                messages?.length || 0,
                MessageType.SITE_OVERVIEW,
                {},
              );
              setDebugMessageVar(newMessage as any);
              setMessagesVar((prev) => [...(prev || []), newMessage as any]);
            }}
          >
            Site overview
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              const newMessage = getQuestion(
                conversation?.id || "",
                messages?.length || 0,
                MessageType.REVIEW_PROPOSAL,
                {
                  expert_to_render: {
                    expert_name: "project_details",
                  },
                },
              );
              setDebugMessageVar(newMessage as any);
              setMessagesVar((prev) => [...(prev || []), newMessage as any]);
            }}
          >
            Review proposal
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              const newMessage = getQuestion(
                conversation?.id || "",
                messages?.length || 0,
                MessageType.FINALISE_DETAILS,
                {},
              );
              setDebugMessageVar(newMessage as any);
              setMessagesVar((prev) => [...(prev || []), newMessage as any]);
            }}
          >
            Finalise details
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              const newMessage = getQuestion(
                conversation?.id || "",
                messages?.length || 0,
                MessageType.OFF_TOPIC,
                {},
              );
              newMessage.text =
                "<div><p>This service doesn't support proposals involving subdivision just yet. For now, please <a href='https://www.yarraranges.vic.gov.au/Development/Planning'>contact Yarra Ranges Council</a> about your enquiry.</p></div>";

              setDebugMessageVar(newMessage as any);
              setMessagesVar((prev) => [...(prev || []), newMessage as any]);
            }}
          >
            Off topic
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              const newMessage = getQuestion(
                conversation?.id || "",
                messages?.length || 0,
                MessageType.SYSTEM_ERROR,
                {},
              );
              newMessage.text =
                "This is a system error message with embedded html <br /><a href='mailto:email@address.com'>email@address.com</a>";

              setDebugMessageVar(newMessage as any);
              setMessagesVar((prev) => [...(prev || []), newMessage as any]);
            }}
          >
            System error
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <SendUserMessageDialog
        onClickSendMessage={handleClickSendMessage}
        onOpenChange={setAddMessageDialogOpen}
        open={addMessageDialogOpen}
      />
    </>
  );
}
